/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { styled, useMediaQuery, useTheme } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';

import Nav from './Nav';
import { Size } from '../../util/types';
import { DRAWER_DIMENSIONS, PAGE_PADDING_SIDE } from '../../util/consts';
import headerBackground from '../../assets/images/header-bg.png';
import { shouldForwardProp } from '../../util/helpers';
import { CartHeader } from '../../features/cart/components/CartHeader';
import NavbarAccountMenu from '../../features/account/containers/NavbarAccountMenu/NavbarAccountMenu';
import TestIds from '../../testIds';

interface AppbarProps extends Record<string, unknown> {
  height: Size;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => shouldForwardProp<AppbarProps>(['height'], prop),
})<AppbarProps>(({ theme, height }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundImage: `url(${headerBackground})`,
  marginLeft: 0,
  width: '100%',
  height: `calc(${height.lg}px + var(--safe-area-statusbarHeight))`,
  display: 'flex',
  alignItems: 'flex-end',
  flexDirection: 'row-reverse',

  padding: `0 20px 0 ${PAGE_PADDING_SIDE.lg}px`,

  [theme.breakpoints.down('lg')]: {
    padding: `0 20px 0 ${PAGE_PADDING_SIDE.md}px`,
  },
  [theme.breakpoints.between('md', 'lg')]: {
    height: `calc(${height.md}px + var(--safe-area-statusbarHeight))`,
  },
}));

const HeaderContentContainer = styled('div')(({ theme }) => ({
  float: 'right',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: '24px',
  [theme.breakpoints.up('md')]: {
    paddingLeft: `min(21vw, ${DRAWER_DIMENSIONS.width.lg + 8}px)`,
  },
}));

type HeaderProps = {
  appbarHeight: Size;
};

const Header = (props: HeaderProps) => {
  const { appbarHeight } = props;
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  if (isMedium) {
    return <Nav />;
  }

  return (
    <>
      <AppBar
        height={appbarHeight}
        position='sticky'
        data-testid={TestIds.components.layout.header.component}
      >
        <HeaderContentContainer>
          <CartHeader />

          <NavbarAccountMenu />
        </HeaderContentContainer>
      </AppBar>

      <Nav />
    </>
  );
};

export default Header;
